/**
 * utils/contacts.js
 * Pure functions for the contacts collection (Contact model redesign). Contacts split into two
 * independent lists that know nothing about each other — PUSAT and CABANG (all branches share
 * the one CABANG list; there is no per-branch list). `data.contacts`:
 * { id, list: 'PUSAT'|'CABANG', businessId, name, addresses: {id,text,isDefault}[], city,
 *   phone, contactPerson, email, keterangan, npwp, pkp, labels, personIds, ekspedisi, internal,
 *   internalBranchId, deactivated }[].
 *
 * `name` is required, unique case-insensitively WITHIN its own list (the same name may exist
 * once in PUSAT and once in CABANG — they are not the same record). `personIds` (SALESPERSON
 * PERSON ids, not roles) requires at least one entry on a normal contact; empty is allowed only
 * on an internal contact. `npwp` is stored exactly as typed — never trimmed, reformatted, or
 * validated; two real formats exist in the source data (16 plain digits, and an older dotted
 * form) and this module does not choose between them. `addresses` may be empty, and with TWO OR
 * MORE entries is NOT required to have one marked `isDefault` — see `defaultAddressText` below
 * for why this module never guesses among several on the caller's behalf. **The one exception:
 * EXACTLY ONE address is always stored as default** (`normAddresses` below) — with only one
 * candidate there is nothing to guess, so this doesn't reopen the guessing this module otherwise
 * refuses to do.
 *
 * **`list` is fixed at creation — never editable afterwards, on any contact.** Mirrors a
 * salesperson role being pinned to its branch for life: a contact filed in the wrong list, or a
 * real firm that needs a presence in both, is never "moved" — a NEW entry is created in the
 * other list instead, and the two are optionally linked via `businessId` if they're the same
 * real firm. `updateContact` refuses (same-reference no-op) any write attempting to change
 * `list`, regardless of what the caller sends — see that function's own comment.
 *
 * A `businessId` links this entry to a real firm (`data.businesses`, see businesses.js) that
 * may also be linked from other entries, in either list, any number of them. Matching an entry
 * to a business is always a deliberate action elsewhere — nothing here infers or creates a link.
 *
 * `internal`/`internalBranchId` mark an app-maintained branch stand-in used only as a TRANSFER's
 * counterparty (see `ensureInternalContacts` below) — never created, edited, or deleted through
 * `addContact`/`updateContact`/`deleteContact`.
 *
 * **`deactivated`** (default `false`): a reversible status flag, set by hand from the contact
 * form — never inferred, never asked "why" (a wrong-list filing, a closed shop, an ended
 * relationship are all just "deactivated," with no reason recorded). A deactivated contact keeps
 * everything it already has (history, transactions, its own visibility on the Kontak page) —
 * the ONE effect is that every `ContactPicker` consumer must exclude it from what it OFFERS, so
 * no NEW transaction/adjustment can pick it (an already-referencing record is unaffected; a
 * consumer that can preload an existing selection must keep that one visible regardless — see
 * `TransactionFormModal`'s own `pickableContacts`, components doc). Not gated on
 * `contactRefCount` in either direction — deactivating and deleting are both always available on
 * an unreferenced contact, and the user picks which. `addContact` always forces `deactivated:
 * false` (a brand-new contact is never created already-deactivated — deactivating is a
 * deliberate follow-up action on an existing record, mirroring `addSalesperson`'s own
 * `active: true` default). Internal contacts can never be deactivated — `updateContact` already
 * rejects every write to one outright, this flag included.
 */
import { generateId } from "./idGenerators";
import { personExists } from "./persons";
import { businessExists } from "./businesses";
import { resolveBranchName } from "./branches";

const VALID_LISTS = new Set(["PUSAT", "CABANG"]);
const VALID_LABELS = new Set(["supplier", "customer", "broker"]);

// An internal contact's own `name` field exists only to satisfy "name is required," the one
// rule every contact shares — it is NEVER real data (see the file header: the real display name
// is resolved live from the branch record, never copied here) and NO display path may read
// `.name` directly on a contact where `internal === true`. Always go through `displayContact`.
const INTERNAL_CONTACT_NAME_PLACEHOLDER = "__internal__";

function norm(s) {
  return String(s || "").trim();
}

/** Exported (contacts Excel import, batch 2 — `contactImportCommit.js`) so a caller that needs to
 *  pre-classify "already exists" BEFORE attempting a write can ask the exact same question
 *  `validateContactFields` asks internally, rather than re-implementing (and risking drifting
 *  from) the app's own duplicate rule. */
export function nameCollides(contacts, list, name, excludeId) {
  const key = norm(name).toLowerCase();
  return contacts.some(
    (c) => c.id !== excludeId && c.list === list && norm(c.name).toLowerCase() === key
  );
}

/** Shape-coerce an addresses array. For TWO OR MORE addresses, never choose a default on the
 *  caller's behalf — zero, one, or (if the caller supplies it that way) more than one entry may
 *  carry `isDefault: true`, passed through unchanged. Picking one among several silently was the
 *  exact bug this redesign removed (see root CLAUDE.md) and stays removed here.
 *
 *  **EXACTLY ONE address is a different case — the default-address rule (added after the contacts
 *  Excel import's own manual-testing pass): a lone address is stored as default even if the
 *  caller didn't mark it.** There is nothing to CHOOSE between when there is only one candidate,
 *  so this is not the guessing the rule above exists to prevent — it fires whether the single
 *  address came from a human leaving only one on the contact form, or an import shop whose several
 *  addresses were resolved down to one earlier in the pipeline (`contactImportResolve.js`'s own
 *  step-3 dedupe). Both `addContact` and `updateContact` call this one function for every write
 *  that touches `addresses`, so the rule applies identically everywhere a contact is saved — no
 *  separate handling exists (or should ever be added) in `contactImportCommit.js` or any form. */
function normAddresses(list) {
  if (!Array.isArray(list)) return [];
  const normed = list.map((a) => ({
    id: a?.id || generateId(),
    text: norm(a?.text),
    isDefault: a?.isDefault === true,
  }));
  if (normed.length === 1 && !normed[0].isDefault) {
    normed[0] = { ...normed[0], isDefault: true };
  }
  return normed;
}

/**
 * Validate a contact write BEFORE calling addContact/updateContact, so a caller can show the
 * real reason a save would fail instead of a silent same-reference no-op. `addContact`/
 * `updateContact` call this themselves too (single source of truth), so the two can never
 * disagree about what's valid. Never reached for an internal contact — those are built directly
 * by `ensureInternalContacts`, not through this path, so `personIds`/etc. are always required
 * here with no internal-contact exception baked in (that exception lives structurally, in
 * internal contacts never reaching this function at all).
 */
export function validateContactFields(data, fields, { excludeId = null } = {}) {
  const contacts = data.contacts || [];
  const persons = data.persons || [];
  const businesses = data.businesses || [];

  const list = fields?.list;
  if (!VALID_LISTS.has(list)) return { ok: false, error: "Daftar kontak (Pusat/Cabang) wajib diisi." };

  const name = norm(fields?.name);
  if (!name) return { ok: false, error: "Nama wajib diisi." };
  if (nameCollides(contacts, list, name, excludeId)) {
    return { ok: false, error: "Nama kontak sudah dipakai di daftar ini." };
  }

  const personIds = Array.isArray(fields?.personIds) ? fields.personIds : [];
  if (personIds.length === 0) {
    return { ok: false, error: "Pilih minimal satu sales/person." };
  }
  for (const pid of personIds) {
    if (!personExists(persons, pid)) return { ok: false, error: "Sales/person tidak ditemukan." };
  }

  if (fields?.businessId != null && !businessExists(businesses, fields.businessId)) {
    return { ok: false, error: "Bisnis tidak ditemukan." };
  }

  const labels = Array.isArray(fields?.labels) ? fields.labels : [];
  for (const l of labels) {
    if (!VALID_LABELS.has(l)) return { ok: false, error: `Label kontak tidak dikenal: ${l}.` };
  }

  return { ok: true, error: null };
}

/**
 * Add a new normal contact. Same-reference no-op when `validateContactFields` rejects the
 * input (blank/colliding name, missing/invalid list, no personIds, an unresolvable personId/
 * businessId, or an unknown label) — always same-reference, never partially applied.
 * `internal`/`internalBranchId` are never settable here — always forced `false`/`null`.
 * `deactivated` is always forced `false` too — a brand-new contact is never created already
 * deactivated; that's a deliberate follow-up action on an existing record (see the file header).
 */
export function addContact(data, fields) {
  const check = validateContactFields(data, fields);
  if (!check.ok) return data;

  const contacts = data.contacts || [];
  const contact = {
    id: generateId(),
    list: fields.list,
    businessId: fields.businessId || null,
    name: norm(fields.name),
    addresses: normAddresses(fields.addresses),
    city: norm(fields.city),
    phone: norm(fields.phone),
    contactPerson: norm(fields.contactPerson),
    email: norm(fields.email),
    keterangan: norm(fields.keterangan),
    npwp: fields.npwp != null ? String(fields.npwp) : "", // exactly as typed — no trim/reformat
    pkp: fields.pkp === true,
    labels: Array.isArray(fields.labels) ? [...new Set(fields.labels)] : [],
    personIds: [...new Set(fields.personIds)],
    ekspedisi: Array.isArray(fields.ekspedisi) ? fields.ekspedisi.map(norm).filter(Boolean) : [],
    internal: false,
    internalBranchId: null,
    deactivated: false,
  };
  return { ...data, contacts: [...contacts, contact] };
}

/**
 * Update an existing normal contact. Same-reference no-op when `id` doesn't resolve, the
 * current record is internal (not editable through this path — see `ensureInternalContacts`),
 * the caller attempts to change `list` (see below), or the merged result fails
 * `validateContactFields` (excluding the contact's own current name from the collision check,
 * same as before this redesign).
 *
 * **`list` is immutable once a contact exists — refuses the WHOLE write (same-reference no-op)
 * if `fields.list` is present and differs from the contact's current list, regardless of what
 * the form sends.** `merged.list` below always reads `current.list`, never `fields.list`, as a
 * second line of defense — even if the guard above were ever loosened by mistake, this line
 * alone still can't move a contact between lists. A contact that belongs in the other list is
 * never "moved": the caller creates a NEW entry there instead (optionally linked to this one via
 * `businessId` if they're the same real firm).
 */
export function updateContact(data, id, fields) {
  const contacts = data.contacts || [];
  const idx = contacts.findIndex((c) => c.id === id);
  if (idx === -1) return data;

  const current = contacts[idx];
  if (current.internal) return data;
  if (fields?.list != null && fields.list !== current.list) return data;

  const merged = {
    list: current.list, // fixed at creation — never taken from `fields`, see this function's header
    businessId: fields?.businessId !== undefined ? (fields.businessId || null) : current.businessId,
    name: fields?.name != null ? norm(fields.name) : current.name,
    addresses: fields?.addresses != null ? normAddresses(fields.addresses) : current.addresses,
    city: fields?.city != null ? norm(fields.city) : current.city,
    phone: fields?.phone != null ? norm(fields.phone) : current.phone,
    contactPerson: fields?.contactPerson != null ? norm(fields.contactPerson) : current.contactPerson,
    email: fields?.email != null ? norm(fields.email) : current.email,
    keterangan: fields?.keterangan != null ? norm(fields.keterangan) : current.keterangan,
    npwp: fields?.npwp != null ? String(fields.npwp) : current.npwp,
    pkp: fields?.pkp != null ? fields.pkp === true : current.pkp,
    labels: fields?.labels != null ? [...new Set(fields.labels)] : current.labels,
    personIds: fields?.personIds != null ? [...new Set(fields.personIds)] : current.personIds,
    ekspedisi: fields?.ekspedisi != null ? fields.ekspedisi.map(norm).filter(Boolean) : current.ekspedisi,
    deactivated: fields?.deactivated != null ? fields.deactivated === true : current.deactivated,
  };

  const check = validateContactFields(data, merged, { excludeId: id });
  if (!check.ok) return data;

  const next = { ...current, ...merged, internal: false, internalBranchId: null };
  return { ...data, contacts: contacts.map((c, i) => (i === idx ? next : c)) };
}

/** Number of history entries OR transactions referencing this contact — a transaction's own
 *  contactId is never mirrored into `data.history` (transactions log through their own write
 *  path), so both collections must be counted for this guard to actually protect a contact
 *  referenced only by a transaction. Unaffected by the shape redesign. */
export function contactRefCount(data, id) {
  const historyCount = (data.history || []).filter((h) => h.contactId === id).length;
  const txnCount = (data.transactions || []).filter((t) => t.contactId === id).length;
  return historyCount + txnCount;
}

/** Delete only when: the contact is not internal (not deletable through this path — see
 *  `removeInternalContactForBranch` for the app's own cleanup path), AND unreferenced
 *  (contactRefCount === 0). Same-reference no-op otherwise. */
export function deleteContact(data, id) {
  const contacts = data.contacts || [];
  const contact = contacts.find((c) => c.id === id);
  if (!contact) return data;
  if (contact.internal) return data;
  if (contactRefCount(data, id) > 0) return data;
  return { ...data, contacts: contacts.filter((c) => c.id !== id) };
}

/** The text of whichever address is marked `isDefault`, or "" when none is (an empty list, or a
 *  non-empty one where nothing has been marked yet). Never guesses the first entry — a caller
 *  needing a default address today, before a human has picked one, gets nothing rather than a
 *  silently-chosen value (see root CLAUDE.md's Contact entry). */
export function defaultAddressText(contact) {
  const addr = (contact?.addresses || []).find((a) => a.isDefault);
  return addr ? addr.text : "";
}

/** Display label for a contact. An internal contact's `.name` is never read here (see the
 *  placeholder-name comment above): the CABANG-list "Pusat" stand-in always shows the literal
 *  "Pusat"; a PUSAT-list branch stand-in resolves its branch's CURRENT name live via
 *  `resolveBranchName` and shows "" (never a fabricated/asserted sentence) when that branch
 *  can't be resolved — the branch is usually not actually gone, just not yet looked up with the
 *  right `branches` list, so nothing here claims otherwise. A normal contact is unaffected:
 *  "name — city" when city is set, else just name. `branches` defaults to `[]` so every
 *  existing call site keeps compiling; only the internal-contact rows are affected until each
 *  call site is updated to pass the real list. */
export function displayContact(c, branches = []) {
  if (c?.internal) {
    if (c.list === "CABANG") return "Pusat";
    return resolveBranchName(branches, c.internalBranchId) || "";
  }
  const city = norm(c?.city);
  return city ? `${c.name} — ${city}` : c.name;
}

/** Find the one app-maintained internal contact for a given list (+ branch, for PUSAT), or null.
 *  `internalBranchId` is ignored for `list === "CABANG"` (that stand-in is branch-independent —
 *  pass `null`). */
export function findInternalContact(contacts, list, internalBranchId = null) {
  return (
    (contacts || []).find(
      (c) => c.internal && c.list === list && c.internalBranchId === (list === "CABANG" ? null : internalBranchId)
    ) || null
  );
}

function makeInternalContact(list, internalBranchId) {
  return {
    id: generateId(),
    list,
    businessId: null,
    name: INTERNAL_CONTACT_NAME_PLACEHOLDER,
    addresses: [],
    city: "",
    phone: "",
    contactPerson: "",
    email: "",
    keterangan: "",
    npwp: "",
    pkp: false,
    labels: [],
    personIds: [],
    ekspedisi: [],
    internal: true,
    internalBranchId,
    deactivated: false, // never settable — updateContact rejects every write to an internal contact outright
  };
}

/**
 * Ensure the app-maintained internal contacts exist: exactly one CABANG-list stand-in for
 * "Pusat" (branch-independent — a transfer only ever runs Pusat <-> one Cabang branch, never
 * branch-to-branch, so the CABANG side never needs more than one), plus exactly one PUSAT-list
 * stand-in per entry in `data.branches`. Idempotent, same-reference no-op when nothing is
 * missing. Never removes anything (a branch removed elsewhere is cleaned up explicitly by
 * `removeInternalContactForBranch`, not by this function re-running).
 */
export function ensureInternalContacts(data) {
  const contacts = data.contacts || [];
  const branches = data.branches || [];
  let next = contacts;
  let changed = false;

  if (!next.some((c) => c.internal && c.list === "CABANG")) {
    next = [...next, makeInternalContact("CABANG", null)];
    changed = true;
  }

  for (const b of branches) {
    if (!next.some((c) => c.internal && c.list === "PUSAT" && c.internalBranchId === b.id)) {
      next = [...next, makeInternalContact("PUSAT", b.id)];
      changed = true;
    }
  }

  return changed ? { ...data, contacts: next } : data;
}

/** App-only cleanup for a branch that has just been deleted: removes that branch's PUSAT-list
 *  internal contact. Deliberately bypasses `deleteContact`'s internal-contact guard — this is
 *  the app's own maintenance, not a normal delete path. Safe unconditionally: `deleteBranch`
 *  only ever succeeds while `branchRefCount(data, id) === 0` (zero transactions reference the
 *  branch), and the ONLY way this internal contact could carry a real `contactRefCount` is via a
 *  transaction that also stamps that same `branchId` — so a branch that was deletable can never
 *  have left its internal contact referenced either. */
export function removeInternalContactForBranch(data, branchId) {
  const contacts = data.contacts || [];
  const next = contacts.filter((c) => !(c.internal && c.list === "PUSAT" && c.internalBranchId === branchId));
  if (next.length === contacts.length) return data;
  return { ...data, contacts: next };
}
